import type { UseComboboxPropGetters } from "downshift";
import { useCombobox } from "downshift";
import { uniqBy } from "lodash-es";
import { memo, useMemo, useRef } from "react";
import { twJoin } from "tailwind-merge";

import { autoCompleteDisabledProperties } from "@/helpers/auto-complete-helpers";
import { twResolve } from "@/helpers/tw-resolve";

import { fieldControlStateVariants } from "./field-control";
import { InputExtra } from "./Input";

export interface InputAutocompleteProps extends Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  "id" | "placeholder" | "disabled" | "readOnly"
> {
  value: string;
  suggestions: Suggestion[];
  "aria-invalid"?: boolean;
  onBlur?: () => void;
  postfix?: React.ReactNode;
  onChange?: (value: string) => void;
}

export function InputAutocomplete({
  id,
  value,
  disabled,
  placeholder,
  suggestions,
  onChange,
  readOnly,
  postfix,
  "aria-invalid": isInvalid,
}: InputAutocompleteProps): React.ReactNode {
  const inputValueRef = useRef<HTMLInputElement>(null);
  const filteredItems = useMemo(() => {
    if (!value) {
      if (suggestions.length > 8) {
        return suggestions;
      }
    }

    const lowerCaseValue = value.toLowerCase();

    return suggestions.filter((item) => item.searchableValue.includes(lowerCaseValue));
  }, [value, suggestions]);
  const { isOpen, getMenuProps, getInputProps, highlightedIndex, getItemProps, getLabelProps, closeMenu } = useCombobox(
    {
      inputValue: value,
      defaultHighlightedIndex: 0,
      items: filteredItems,
      onSelectedItemChange(changes) {
        onChange?.(changes.selectedItem?.displayValue || "");
        closeMenu();
      },
      onStateChange: ({ type, selectedItem }) => {
        switch (type) {
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
            if (selectedItem) {
              onChange?.(selectedItem?.displayValue ?? "");
            } else {
              inputValueRef.current?.form?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
            }
            closeMenu();
            break;
          case useCombobox.stateChangeTypes.ItemClick:
            onChange?.(selectedItem?.displayValue ?? "");
            closeMenu();
            break;
          case useCombobox.stateChangeTypes.InputBlur:
            closeMenu();
            break;
        }
      },
    },
  );

  const isMenuVisible = isOpen && filteredItems.length > 0;

  return (
    <div className={twJoin("relative", isMenuVisible && "z-raised")} data-testid="input-autocomplete">
      <div>
        <div
          {...getLabelProps()}
          className={twResolve(
            "relative flex min-h-control-md w-full cursor-default flex-wrap items-center rounded-lg border border-border-secondary bg-background-default text-left leading-autocomplete-control",
            fieldControlStateVariants({ invalid: isInvalid, focus: "within" }),
            isMenuVisible ? "rounded-b-none" : undefined,
            (disabled || readOnly) && "border-border-secondary bg-background-secondary hover:border-border-secondary",
          )}
        >
          <input
            // oxlint-disable-next-line react-hooks/refs
            {...getInputProps({
              onChange: (e) => onChange?.(e.currentTarget.value),
              ref: inputValueRef,
            })}
            className={twResolve(
              "h-control-md w-autocomplete-input-collapsed min-w-autocomplete-input flex-1 rounded-lg px-field-inline py-space-xs text-body text-text-primary placeholder:text-text-tertiary focus:outline-hidden",
              (disabled || readOnly) && "text-text-secondary",
            )}
            id={id}
            aria-invalid={isInvalid}
            placeholder={placeholder}
            readOnly={readOnly}
            disabled={disabled || readOnly}
            {...autoCompleteDisabledProperties}
          />
        </div>
        {postfix && <InputExtra position="end">{postfix}</InputExtra>}
      </div>
      <ul
        {...getMenuProps()}
        className={twJoin(
          "absolute flex max-h-autocomplete-menu min-h-control-sm w-full flex-col items-start overflow-auto rounded-lg rounded-t-none border border-t-0 border-border-brand-secondary bg-background-default shadow-autocomplete-menu hocus:outline-hidden",
          isOpen ? "opacity-100" : "opacity-0",
          filteredItems.length === 0 ? "hidden" : undefined,
        )}
      >
        {isOpen &&
          filteredItems.map((item, index) => (
            <ListItem
              key={item.displayValue}
              item={item}
              index={index}
              isHighlighted={highlightedIndex === index}
              getItemProps={getItemProps}
            />
          ))}
      </ul>
    </div>
  );
}

const ListItem = memo(ListItemInner) as typeof ListItemInner;

function ListItemInner({
  item,
  index,
  isHighlighted,
  getItemProps,
}: {
  item: Suggestion;
  index: number;
  isHighlighted: boolean;
  getItemProps: UseComboboxPropGetters<Suggestion>["getItemProps"];
}) {
  return (
    <li
      data-testid="input-autocomplete-item"
      {...getItemProps({ index, item })}
      className={twJoin(
        "w-full cursor-pointer px-select-option-inline py-select-option-block text-text-primary select-none first:mt-select-menu-edge last:mb-select-menu-edge",
        isHighlighted ? "bg-background-brand-light" : undefined,
      )}
    >
      <div className="flex items-center">{item.displayValue}</div>
    </li>
  );
}

type Suggestion = { searchableValue: string; displayValue: string };

export function prepareSuggestions(items: string[]): Suggestion[] {
  return uniqBy(
    items.map((x) => ({ searchableValue: x.trim().toLowerCase(), displayValue: x })).filter((x) => x.searchableValue),
    (x) => x.displayValue,
  );
}
